import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  RefreshCcw, DollarSign, Clock, ChevronLeft, ChevronRight, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminRefunds() {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRefund, setSelectedRefund] = useState(null);

  const loadRefunds = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/refunds?page=${page}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRefunds(res.data.refunds || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Failed to load refunds:', error);
      toast.error('Failed to load refunds');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadRefunds();
  }, [loadRefunds]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getEventTypeBadge = (eventType) => {
    const badges = {
      'admin_refund': { label: 'Admin Refund', color: 'bg-[#00F0FF]/20 text-[#00F0FF]' },
      'charge_refunded': { label: 'Stripe Refund', color: 'bg-blue-500/20 text-blue-400' },
      'partial_refund': { label: 'Partial', color: 'bg-yellow-500/20 text-yellow-400' },
    };
    return badges[eventType] || { label: eventType || 'Refund', color: 'bg-neutral-500/20 text-neutral-400' };
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <RefreshCcw className="w-8 h-8 text-yellow-500" />
              Refunds
            </h1>
            <p className="text-neutral-400 mt-1">Review refund transactions and commission reversals</p>
          </div>
          
          <Button
            onClick={loadRefunds}
            variant="outline"
            className="border-white/20"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
          </div>
        ) : refunds.length === 0 ? (
          <div className="text-center py-12 text-neutral-400">
            <RefreshCcw className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No refunds recorded</p>
          </div>
        ) : (
          <>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Type</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Order</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Amount</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Reason</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Date</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {refunds.map((refund, idx) => {
                      const typeBadge = getEventTypeBadge(refund.event_type);
                      return (
                        <tr key={refund.event_id || refund.refund_id || `refund-${idx}`} className="hover:bg-white/5">
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${typeBadge.color}`}>
                              {typeBadge.label}
                            </span>
                          </td>
                          <td className="p-4 font-mono text-sm">
                            {refund.order_id?.slice(0, 16)}...
                          </td>
                          <td className="p-4 font-semibold text-red-400">
                            -{formatCurrency(refund.amount || refund.refund_amount)}
                          </td>
                          <td className="p-4 text-sm text-neutral-400 max-w-xs truncate">
                            {refund.reason || refund.refund_reason || 'N/A'}
                          </td>
                          <td className="p-4 text-sm text-neutral-400">
                            {formatDate(refund.created_at)}
                          </td>
                          <td className="p-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedRefund(refund)}
                              className="border-white/20"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="border-white/20"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-4 py-2 text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="border-white/20"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Refund Detail Modal */}
        <Dialog open={!!selectedRefund} onOpenChange={() => setSelectedRefund(null)}>
          <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCcw className="w-5 h-5 text-yellow-500" />
                Refund Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedRefund && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-neutral-400">Event Type</div>
                    <div>{getEventTypeBadge(selectedRefund.event_type).label}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Amount</div>
                    <div className="font-semibold text-red-400">
                      -{formatCurrency(selectedRefund.amount || selectedRefund.refund_amount)}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Order ID</div>
                    <div className="font-mono text-xs">{selectedRefund.order_id}</div>
                  </div>
                  <div>
                    <div className="text-neutral-400">Date</div>
                    <div>{formatDate(selectedRefund.created_at)}</div>
                  </div>
                </div>

                {selectedRefund.reason && (
                  <div>
                    <div className="text-neutral-400 text-sm mb-1">Reason</div>
                    <div className="bg-black/30 p-2 rounded text-sm">{selectedRefund.reason}</div>
                  </div>
                )}

                {selectedRefund.stripe_refund_id && (
                  <div>
                    <div className="text-neutral-400 text-sm mb-1">Stripe Refund ID</div>
                    <div className="font-mono text-xs bg-black/30 p-2 rounded">{selectedRefund.stripe_refund_id}</div>
                  </div>
                )}

                {selectedRefund.commission_reversed !== undefined && (
                  <div className="pt-4 border-t border-white/10">
                    <div className="text-neutral-400 text-sm mb-1">Commission Reversal</div>
                    <div className={`text-sm ${selectedRefund.commission_reversed ? 'text-green-400' : 'text-yellow-400'}`}>
                      {selectedRefund.commission_reversed ? '✓ Commission reversed' : '⏳ Pending reversal'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminRefunds;
